import { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BrandLogo } from './BrandLogo';
import { Button } from './Button';

type Props = {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
  /**
   * Enter dispara el boton del pie marcado con `enterAction` (ver `Button`). Por defecto pregunta
   * antes; Ctrl/Cmd+Enter lo ejecuta sin preguntar. `false` lo ejecuta directo (confirmaciones).
   */
  confirmOnEnter?: boolean;
};

type EnterConfirm = { button: HTMLButtonElement; label: string };

// Controles donde Enter ya tiene un significado propio.
const ENTER_OWN_TAGS = new Set(['TEXTAREA', 'SELECT', 'BUTTON', 'A']);

function focusFirstIncompleteField(root: HTMLElement | null) {
  const fields = root?.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
    '[aria-invalid="true"], input[required], select[required], textarea[required]',
  );
  const target = Array.from(fields ?? []).find(
    (field) => field.getAttribute('aria-invalid') === 'true' || !field.value,
  );
  target?.focus();
}

let modalStack: number[] = [];
let modalCounter = 0;
const modalStackListeners = new Set<() => void>();

function notifyModalStackChange() {
  modalStackListeners.forEach((listener) => listener());
}

function syncBodyModalState() {
  if (typeof document === 'undefined') return;
  const hasOpenModals = modalStack.length > 0;
  document.body.classList.toggle('modal-open', hasOpenModals);
}

function pushModal(modalId: number) {
  if (modalStack.includes(modalId)) return;
  modalStack = [...modalStack, modalId];
  syncBodyModalState();
  notifyModalStackChange();
}

function removeModal(modalId: number) {
  const next = modalStack.filter((id) => id !== modalId);
  if (next.length === modalStack.length) return;
  modalStack = next;
  syncBodyModalState();
  notifyModalStackChange();
}

function topModalId() {
  return modalStack.length ? modalStack[modalStack.length - 1] : null;
}

export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  className,
  confirmOnEnter = true,
}: Props) {
  const modalIdRef = useRef<number>(0);
  const modalCardRef = useRef<HTMLDivElement | null>(null);
  if (modalIdRef.current === 0) {
    modalCounter += 1;
    modalIdRef.current = modalCounter;
  }

  const [, setStackVersion] = useState(0);
  const [enterConfirm, setEnterConfirm] = useState<EnterConfirm | null>(null);

  useEffect(() => {
    if (!open) setEnterConfirm(null);
  }, [open]);
  useEffect(() => {
    const listener = () => setStackVersion((value) => value + 1);
    modalStackListeners.add(listener);
    return () => {
      modalStackListeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    const modalId = modalIdRef.current;
    if (open) {
      pushModal(modalId);
      return () => removeModal(modalId);
    }
    removeModal(modalId);
    return undefined;
  }, [open]);

  const modalId = modalIdRef.current;
  const stackIndex = modalStack.indexOf(modalId);
  const isTopModal = open && topModalId() === modalId;

  useEffect(() => {
    if (!open || !isTopModal) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Enter' || event.defaultPrevented || event.isComposing) return;
      if (event.altKey || event.shiftKey) return;
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (
        target &&
        (ENTER_OWN_TAGS.has(target.tagName) ||
          target.isContentEditable ||
          target.getAttribute('aria-expanded') === 'true' ||
          target.closest('[data-enter-ignore]'))
      ) {
        return;
      }
      // Calendario de react-datepicker abierto: Enter elige la fecha.
      if (document.querySelector('.react-datepicker-popper')) return;
      const card = modalCardRef.current;
      const action = card?.querySelector<HTMLButtonElement>('.modal-footer [data-enter-action]');
      if (!action) return;
      event.preventDefault();
      if (action.disabled) {
        focusFirstIncompleteField(card?.querySelector('.modal-body') ?? null);
        return;
      }
      if (!confirmOnEnter || event.ctrlKey || event.metaKey) {
        action.click();
        return;
      }
      setEnterConfirm({ button: action, label: action.textContent?.trim() || 'Guardar' });
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, isTopModal, onClose, confirmOnEnter]);

  useEffect(() => {
    if (!open || !isTopModal) return;
    const frame = window.requestAnimationFrame(() => {
      const body = modalCardRef.current?.querySelector<HTMLElement>('.modal-body');
      if (body) {
        body.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
      const firstFocusable = body?.querySelector<HTMLElement>(
        [
          'input:not([type="hidden"]):not([disabled])',
          'select:not([disabled])',
          'textarea:not([disabled])',
          'button:not([disabled])',
          '[tabindex]:not([tabindex="-1"])',
        ].join(','),
      );
      if (!firstFocusable) return;
      firstFocusable.focus({ preventScroll: true });
      if (firstFocusable instanceof HTMLInputElement || firstFocusable instanceof HTMLTextAreaElement) {
        firstFocusable.select?.();
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [open, isTopModal]);

  if (!open) return null;
  const backdropClass = [
    'modal-backdrop',
    className?.includes('modal-full') ? 'modal-backdrop--full' : null,
  ]
    .filter(Boolean)
    .join(' ');

  const zIndexBase = 50 + (stackIndex < 0 ? 0 : stackIndex * 2);
  const modalNode = (
    <div
      className={[backdropClass, !isTopModal ? 'modal-backdrop--inactive' : null].filter(Boolean).join(' ')}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      aria-hidden={!isTopModal}
      style={{ zIndex: zIndexBase }}
    >
      <div ref={modalCardRef} className={['modal-card', className].filter(Boolean).join(' ')}>
        <div className="modal-header">
          <div className="modal-title">
            <span className="modal-title-brand" aria-hidden="true">
              <BrandLogo variant="icon" alt="" />
            </span>
            <h3>{title}</h3>
          </div>
          <button type="button" className="ghost" onClick={onClose} aria-label="Cerrar modal">
            ✕
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );

  const closeEnterConfirm = () => setEnterConfirm(null);
  const runEnterConfirm = () => {
    const button = enterConfirm?.button;
    setEnterConfirm(null);
    if (button?.isConnected && !button.disabled) button.click();
  };
  const shortcut = /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘ + Enter' : 'Ctrl + Enter';

  return (
    <>
      {createPortal(<div data-modal-stack-index={stackIndex}>{modalNode}</div>, document.body)}
      {enterConfirm ? (
        <Modal
          open
          title="¿Guardar los cambios?"
          onClose={closeEnterConfirm}
          confirmOnEnter={false}
          className="modal-card--confirm"
          footer={
            <>
              <Button variant="outline" onClick={closeEnterConfirm}>
                Seguir editando
              </Button>
              <Button enterAction onClick={runEnterConfirm}>
                {enterConfirm.label}
              </Button>
            </>
          }
        >
          <p>
            Presionaste Enter en <strong>{title}</strong>. Se ejecutará «{enterConfirm.label}» con los
            datos del formulario.
          </p>
          <p className="page-subtitle">
            Enter confirma, Escape vuelve al formulario. {shortcut} guarda sin preguntar.
          </p>
        </Modal>
      ) : null}
    </>
  );
}
